import { SUPPORT_TICKETS, PRACTICE_TICKET } from '../domains/support/tickets.js';
import { replayPracticeEvents } from '../domains/support/endpoint.js';
import { guidedValidation, sanitizeGuidedValue } from './guidedTicket.js';

const KEY = 'support-track';
const blank = () => ({ tickets: {} });

/** Version of a ticket's guided steps (1 unless the ticket says otherwise). */
export const ticketVersion = (ticket) => ticket?.guidedVersion || 1;
export const recordVersion = (record) => record?.guidedVersion || 1;

/**
 * Unfinished work saved for an older version of a ticket's steps cannot be checked against
 * the new steps, so that ticket alone restarts. Completed tickets are never touched, and
 * nothing else in the record changes. Returns the ids that were restarted.
 */
export function restartOutdatedTickets(state) {
  const restarted = [];
  for (const t of SUPPORT_TICKETS) {
    const r = state?.tickets?.[t.id];
    if (!r || r.status === 'complete' || recordVersion(r) === ticketVersion(t)) continue;
    state.tickets[t.id] = { guidedVersion: ticketVersion(t), restarted: true };
    restarted.push(t.id);
  }
  return restarted;
}
const normalized = (s) => String(s || '').toLowerCase();

export function supportValidation(ticket, record = {}) {
  if (ticket.guided) return guidedValidation(ticket, record);
  const answers = record.answers || {};
  const missing = ['symptom','impact','scope','firstCheck','evidence','assessment','disposition','handoff']
    .filter((field) => String(answers[field] || '').trim().length < 8);
  const evidence = record.evidence || [];
  if (!evidence.length) missing.push('command evidence');
  const corpus = normalized([...Object.values(answers), ...evidence.map((e) => e.command + ' ' + e.output)].join(' '));
  const unmet = (ticket.keywords || []).filter((group) => group.includes('&')
    ? !group.split('&').every((word) => corpus.includes(word))
    : !group.split('|').some((word) => corpus.includes(word)));
  const handoff = normalized(answers.handoff);
  const handoffMissing = (ticket.handoffKeywords || []).filter((word) => !handoff.includes(word));
  if (handoffMissing.length) unmet.push(`handoff: ${handoffMissing.join(', ')}`);
  return { ok: missing.length === 0 && unmet.length === 0, missing, unmet };
}

const PRACTICE_CHOICES = { q1: 'B', q2: 'A', q3: 'B', q4: 'B', q5: 'A' };
const PRACTICE_CAUSE = /\b(cable|unplugged|media disconnected|physical)\b/i;
const PRACTICE_CONFIRMATION = /\b(ping|gateway|repl(?:y|ies))\b/i;

export function checkPracticeNote(note = '') {
  const text = String(note || '').trim();
  const cause = PRACTICE_CAUSE.test(text);
  const confirmation = PRACTICE_CONFIRMATION.test(text);
  return { ok: cause && confirmation, cause, confirmation };
}

export function practiceValidation(record = {}) {
  const answers = record.answers || {};
  const missing = Object.entries(PRACTICE_CHOICES)
    .filter(([question, expected]) => answers[question] !== expected)
    .map(([question]) => `correct ${question} answer`);
  const replay = replayPracticeEvents(record.events);
  const reconnectIndex = (record.events || []).findIndex((event) => event?.type === 'reconnect');
  const ipconfig = replay.evidence.some((event) => event.command.trim().toLowerCase() === 'ipconfig'
    && !event.connected && /Media State.*:\s*Media disconnected/i.test(event.output));
  const ping = replay.evidence.some((event) => event.command.trim().toLowerCase() === 'ping 192.168.10.1'
    && event.connected && event.eventIndex > reconnectIndex
    && /Reply from 192\.168\.10\.1/i.test(event.output));
  if (!ipconfig) missing.push('ipconfig while disconnected');
  if (reconnectIndex < 0 || !replay.connected) missing.push('reconnected cable');
  if (!ping) missing.push('successful gateway ping after reconnection');
  const note = checkPracticeNote(record.note);
  if (!note.cause) missing.push('ticket note naming the cable or physical-link problem');
  if (!note.confirmation) missing.push('ticket note confirming the fix with ping, gateway or reply evidence');
  return { ok: missing.length === 0, missing, unmet: [] };
}

export function createSupportProgress(st, { admin = false } = {}) {
  let state;
  const save = () => st.set(KEY, state);
  const load = () => { state = st.get(KEY) || blank(); state.tickets ||= {}; if (restartOutdatedTickets(state).length) save(); };
  load();
  const record = (id) => state.tickets[id] || {};
  const ticketOf = (id) => SUPPORT_TICKETS.find((t) => t.id === id);
  /** Stamp the step version and drop the one-time "restarted" notice once the learner works on it again. */
  const touch = (id, r) => { r.guidedVersion = ticketVersion(ticketOf(id)); delete r.restarted; delete r.redo; return r; };
  const index = (id) => SUPPORT_TICKETS.findIndex((t) => t.id === id);
  return {
    store: st,
    reload() { load(); },
    raw() { return JSON.parse(JSON.stringify(state)); },
    ticketState(id) {
      const i = index(id);
      if (i < 0) return 'locked';
      if (record(id).status === 'complete') return 'complete';
      if (admin) return 'available';
      return i === 0 || SUPPORT_TICKETS.slice(0, i).every((t) => record(t.id).status === 'complete') ? 'available' : 'locked';
    },
    currentTicket() { return SUPPORT_TICKETS.find((t) => this.ticketState(t.id) === 'available') || null; },
    ticketRecord: (id) => JSON.parse(JSON.stringify(record(id))),
    practiceRecord() { return JSON.parse(JSON.stringify(state.practice || {})); },
    savePracticeAnswer(question, choice) {
      if (!Object.hasOwn(PRACTICE_CHOICES, question) || !/^[A-D]$/.test(String(choice || ''))) return false;
      const r = state.practice || {}; r.answers = { ...(r.answers || {}), [question]: choice }; state.practice = r; save(); return true;
    },
    savePracticeNote(note) {
      const r = state.practice || {}; r.note = String(note || '').slice(0, 4000); state.practice = r; save(); return true;
    },
    savePracticeStep(step) {
      const r = state.practice || {}; r.step = Math.max(1, Math.min(4, Number(step) || 1)); state.practice = r; save(); return true;
    },
    addPracticeEvent(event) {
      if (event?.type !== 'reconnect' && !(event?.type === 'command' && typeof event.command === 'string' && event.command.trim() && event.command.length <= 120)) return false;
      const r = state.practice || {}; const events = Array.isArray(r.events) ? r.events.slice(0, 39) : [];
      events.push(event.type === 'reconnect' ? { type: 'reconnect' } : { type: 'command', command: event.command.trim() });
      r.events = events; state.practice = r; save(); return true;
    },
    validatePractice() {
      const result = practiceValidation(state.practice || {});
      const r = state.practice || {}; r.validation = result; state.practice = r; save(); return result;
    },
    completePractice() {
      const result = this.validatePractice();
      if (!result.ok) return false;
      const r = state.practice || {}; r.status = 'complete'; r.completedAt = Date.now(); r.test = false; r.bypass = false; r.validated = true; state.practice = r; save(); return true;
    },
    bypassCompletePractice() {
      if (!admin) return false;
      const r = state.practice || {}; r.status = 'complete'; r.test = true; r.bypass = true; r.validated = false; r.completedAt = Date.now(); state.practice = r; save(); return true;
    },
    resetPractice() { if (!admin) return false; delete state.practice; save(); return true; },
    saveAnswer(id, field, value) {
      if (!admin && this.ticketState(id) !== 'available') return false;
      const r = touch(id, record(id)); r.answers = { ...r.answers, [field]: value }; state.tickets[id] = r; save(); return true;
    },
    saveGuided(id, stepId, value) {
      if (!admin && this.ticketState(id) !== 'available') return false;
      const step = SUPPORT_TICKETS.find((t) => t.id === id)?.guided?.find((s) => s.id === stepId);
      const clean = sanitizeGuidedValue(step, value);
      if (clean === undefined) return false;
      const r = touch(id, record(id)); r.guided = { ...(r.guided || {}), [stepId]: clean }; state.tickets[id] = r; save(); return true;
    },
    addEvidence(id, command, output) {
      if (!admin && this.ticketState(id) !== 'available') return false;
      const r = touch(id, record(id)); r.evidence = [...(r.evidence || []), { command, output, capturedAt: Date.now() }]; state.tickets[id] = r; save(); return true;
    },
    validate(id) {
      const ticket = SUPPORT_TICKETS.find((t) => t.id === id);
      const result = ticket ? supportValidation(ticket, record(id)) : { ok: false, missing: ['ticket'], unmet: [] };
      const r = record(id); r.validation = result; state.tickets[id] = r; save();
      return result;
    },
    complete(id) {
      if (!admin && this.ticketState(id) !== 'available') return false;
      const result = this.validate(id);
      if (!result.ok) return false;
      const r = record(id); r.status = 'complete'; r.completedAt = Date.now(); r.test = false; r.bypass = false; r.validated = true; state.tickets[id] = r; save(); return true;
    },
    bypassComplete(id) {
      if (!admin || index(id) < 0) return false;
      const r = record(id); r.status = 'complete'; r.test = true; r.bypass = true; r.validated = false; r.completedAt = Date.now(); state.tickets[id] = r; save(); return true;
    },
    resetTicket(id) {
      if (!admin || index(id) < 0) return false;
      for (const t of SUPPORT_TICKETS.slice(index(id))) delete state.tickets[t.id];
      save(); return true;
    },
    /**
     * Learner "Redo to verify": clear ONE completed ticket the server could not verify (saved for
     * older steps, or no longer passing the checks) so it can be done again. Later tickets keep
     * their work and are verified once this one is. Only the learner's own record changes.
     */
    redoTicket(id) {
      if (index(id) < 0 || record(id).status !== 'complete') return false;
      state.tickets[id] = { guidedVersion: ticketVersion(ticketOf(id)), redo: true };
      save(); return true;
    },
    /** Same for the practice ticket. */
    redoPractice() {
      if (state.practice?.status !== 'complete') return false;
      delete state.practice; save(); return true;
    },
    resetTrack() { if (!admin) return false; state = blank(); save(); return true; },
    summary() {
      const done = SUPPORT_TICKETS.filter((t) => record(t.id).status === 'complete').length;
      return { done, total: SUPPORT_TICKETS.length, complete: done === SUPPORT_TICKETS.length };
    },
  };
}
